import React from "react";
import type { EventRegistrationMode } from "../../api/events";
import type { TicketSetInput } from "../../api/ticketSets";
import TicketSetsEditor from "../TicketSetsEditor/TicketSetsEditor";
import {
  emptyTicketRow,
  type TicketDraftRow,
} from "../../utils/ticketModePricing";

interface Props {
  registrationMode: EventRegistrationMode;
  chargesFees: boolean;
  onChargesFeesChange: (value: boolean) => void;
  ticketSets: TicketSetInput[];
  onTicketSetsChange: (sets: TicketSetInput[]) => void;
  ticketRows: TicketDraftRow[];
  onTicketRowsChange: (rows: TicketDraftRow[]) => void;
  eventStartDate: string;
}

export default function EventPricingSection({
  registrationMode,
  chargesFees,
  onChargesFeesChange,
  ticketSets,
  onTicketSetsChange,
  ticketRows,
  onTicketRowsChange,
  eventStartDate,
}: Props) {
  function handleChargesFeesChange(checked: boolean) {
    onChargesFeesChange(checked);
    if (!checked) return;
    if (registrationMode === "tickets" && ticketRows.length === 0) {
      onTicketRowsChange([emptyTicketRow()]);
    }
  }

  const showConference = registrationMode === "conference" && chargesFees;
  const showTickets = registrationMode === "tickets" && chargesFees;

  return (
    <fieldset className="border border-slate-200 p-4 space-y-4 min-w-0 max-w-full overflow-hidden">
      <legend className="px-1 text-sm font-semibold text-slate-900">
        Registration pricing
      </legend>

      {registrationMode === "invitation" ? (
        <p className="text-sm text-slate-600">
          Invitation-only events do not collect payment through EventsHouse.
        </p>
      ) : (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={chargesFees}
            onChange={(e) => handleChargesFeesChange(e.target.checked)}
          />
          <span>This event charges registration fees</span>
        </label>
      )}

      {!chargesFees && registrationMode !== "invitation" && (
        <p className="text-sm text-slate-500">
          Leave unchecked for free registration or pricing configured elsewhere.
        </p>
      )}

      {showConference && (
        <TicketSetsEditor
          sets={ticketSets}
          onChange={onTicketSetsChange}
          eventStartDate={eventStartDate}
        />
      )}

      {showTickets && (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Add one row per ticket type. Prices use the selected currency.
          </p>
          {ticketRows.map((row, index) => (
            <div
              key={row.key}
              className="border border-slate-200 p-3 grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
              <label className="block text-sm sm:col-span-2">
                <span className="font-medium">Name *</span>
                <input
                  className="mt-1 w-full border border-slate-300 p-2 text-sm"
                  value={row.name}
                  onChange={(e) => {
                    const next = [...ticketRows];
                    next[index] = { ...row, name: e.target.value };
                    onTicketRowsChange(next);
                  }}
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="font-medium">Description</span>
                <input
                  className="mt-1 w-full border border-slate-300 p-2 text-sm"
                  value={row.description}
                  onChange={(e) => {
                    const next = [...ticketRows];
                    next[index] = { ...row, description: e.target.value };
                    onTicketRowsChange(next);
                  }}
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium">Price *</span>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  className="mt-1 w-full border border-slate-300 p-2 text-sm"
                  value={row.priceMajor}
                  onChange={(e) => {
                    const next = [...ticketRows];
                    next[index] = { ...row, priceMajor: e.target.value };
                    onTicketRowsChange(next);
                  }}
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium">Currency</span>
                <select
                  className="mt-1 w-full border border-slate-300 p-2 text-sm"
                  value={row.currency}
                  onChange={(e) => {
                    const next = [...ticketRows];
                    next[index] = { ...row, currency: e.target.value };
                    onTicketRowsChange(next);
                  }}
                >
                  <option value="gbp">GBP</option>
                  <option value="usd">USD</option>
                  <option value="eur">EUR</option>
                </select>
              </label>
              <div className="sm:col-span-2">
                <button
                  type="button"
                  className="text-sm text-red-600 underline"
                  onClick={() =>
                    onTicketRowsChange(ticketRows.filter((_, i) => i !== index))
                  }
                  disabled={ticketRows.length <= 1}
                >
                  Remove ticket
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="eh-btn-secondary px-4 py-2 text-sm"
            onClick={() => onTicketRowsChange([...ticketRows, emptyTicketRow()])}
          >
            Add ticket type
          </button>
        </div>
      )}
    </fieldset>
  );
}
